import { Learnable, LearnableValueKind, TemplateName } from 'src/api/learningSessions/types';
import {
  CardData,
  getPresentationScreenTemplate,
  getSelectableVideos,
  getValues,
  SelectedVideo,
  selectVideo,
  TestCardData,
  VideoActorFace,
} from 'src/apps/LearningSession/Common/learnableCards/utils';

export type SessionMedia =
  | {
      kind: 'video';
      src: string;
      autoPlay: boolean;
      actorIds: number[] | null;
      actorFace: VideoActorFace | null;
    }
  | { kind: 'audio'; src: string | null; autoPlay: boolean };

const NO_MEDIA: SessionMedia = { kind: 'audio', src: null, autoPlay: false };

/** True when the learner must pick the correct audio clip — video would spoil that. */
const isAudioChoiceTest = (cardData: TestCardData): boolean => {
  const { response } = cardData;
  if (response.templateName === TemplateName.AUDIO_MULTIPLE_CHOICE) return true;
  return (
    (response.templateName === TemplateName.MULTIPLE_CHOICE ||
      response.templateName === TemplateName.REVERSED_MULTIPLE_CHOICE) &&
    response.responseType === 'Audio'
  );
};

const getPresentationScreen = (learnable: Learnable) => {
  try {
    return getPresentationScreenTemplate(learnable);
  } catch {
    return null;
  }
};

/** Picks a video for a learnable from its presentation screen, if it has one. */
export const selectLearnableVideo = (learnable: Learnable): SelectedVideo | null => {
  const video = getPresentationScreen(learnable)?.video;
  const selected = video ? selectVideo(video) : null;
  return selected?.src ? selected : null;
};

/**
 * Decides what the media panel shows for a card.
 *
 * Media is omitted when it would give away the answer:
 * - text/image prompts (type or select the target word)
 * - audio-choice tests (the video's soundtrack is the answer)
 */
export const getSessionMedia = (
  cardData: CardData,
  learnableVideo: SelectedVideo | null,
): SessionMedia => {
  if (cardData.kind === 'Presentation') {
    if (cardData.videoSrc) {
      return {
        kind: 'video',
        src: cardData.videoSrc,
        autoPlay: true,
        actorIds: cardData.videoActorIds,
        actorFace: cardData.videoActorFace ?? null,
      };
    }
    const itemAudio =
      cardData.item.kind === LearnableValueKind.AUDIO
        ? cardData.item.value
        : cardData.definition.kind === LearnableValueKind.AUDIO
          ? cardData.definition.value
          : null;
    return { kind: 'audio', src: itemAudio ?? cardData.audioSrcs[0] ?? null, autoPlay: true };
  }

  // Picking among audio clips: never show video (or other panel media) alongside.
  if (isAudioChoiceTest(cardData)) {
    return NO_MEDIA;
  }

  const { prompt } = cardData;
  if (prompt.promptType === 'Video') {
    const isLearnableVideo = learnableVideo?.src === prompt.video;
    return {
      kind: 'video',
      src: prompt.video,
      autoPlay: true,
      actorIds: prompt.actorIds ?? null,
      actorFace: isLearnableVideo ? learnableVideo.actorFace : null,
    };
  }
  if (prompt.promptType === 'Audio') {
    return { kind: 'audio', src: prompt.audio, autoPlay: true };
  }
  // Text/image tests: omit the learnable's video so it cannot spoil the answer
  return NO_MEDIA;
};

export const getMediaTrackingType = (media: SessionMedia): 'video' | 'audio' | 'none' => {
  if (media.kind === 'video') return 'video';
  return media.src ? 'audio' : 'none';
};

/** Whether the session should show a media panel (and the split-screen layout). */
export const hasPlayableMedia = (media: SessionMedia): boolean =>
  getMediaTrackingType(media) !== 'none';

/** Every native speaker video, then every audio clip, a learnable has. */
export const getLearnableMedia = (learnable: Learnable): SessionMedia[] => {
  const screen = getPresentationScreen(learnable);
  if (!screen) return [];

  const videos: SessionMedia[] =
    screen.video?.kind === LearnableValueKind.VIDEO
      ? getSelectableVideos(screen.video).map(({ src, actorIds, actorFace }) => ({
          kind: 'video',
          src: src as string,
          autoPlay: true,
          actorIds,
          actorFace,
        }))
      : [];
  const audioSrcs = [screen.item, screen.definition, screen.audio].flatMap(value =>
    value?.kind === LearnableValueKind.AUDIO ? getValues(value) : [],
  );
  const audios: SessionMedia[] = Array.from(new Set(audioSrcs)).map(src => ({
    kind: 'audio',
    src,
    autoPlay: true,
  }));

  return [...videos, ...audios];
};

/**
 * The media a learner can flip through, starting from what the card already shows.
 * Anything the card shows that isn't in the learnable's media goes first.
 */
export const getBrowsableMedia = (current: SessionMedia, learnableMedia: SessionMedia[]) => {
  if (!current.src) return { items: learnableMedia, startIndex: 0 };

  const index = learnableMedia.findIndex(media => media.src === current.src);
  return index === -1
    ? { items: [current, ...learnableMedia], startIndex: 0 }
    : { items: learnableMedia, startIndex: index };
};
